import * as Sentry from '@sentry/react';
import { Button, message, Modal, Tooltip, Typography } from 'antd';
import React, { useRef, useState, useCallback } from 'react';

import { useDataCollBlockedStatus } from '../../hooks/useDataCollBlockedStatus';
import { useTransformation } from '../../hooks/useTransformation';
import { isEditable, isDataEditable } from './blockingUtils';
import { DataCollBasisDownload } from './DataCollBasisDownload';
import UploadTableEditingCard from './dataCollBasisEditing/UploadTableEditingCard';
import { DataCollBasisUpload } from './DataCollBasisUpload';
import { DataCollBasisValidation } from './DataCollBasisValidation';
import DataCollBlockedStatusDisplay from './DataCollBlockedStatusDisplay';
import DataCollSelection from './DataCollSelection';
import DataCollTransformationPanel from './DataCollTransformationPanel';
import TransformationStatus from './TransformationStatus';
import TableMergeData from './dataMerge/TableMergeData';
import { extractYear } from './extractYear';
import RecalculationCard from './RecalculationCard';
import RestorePreviousOriginalTableCard from './RestorePreviousOriginalTableCard';
import TableMergeStructureSection from './structuralMerge/TableMergeStructureSection';
import TableMergeStructureStatusAlert from './structuralMerge/TableMergeStructureStatusAlert';
import type { DataCollBlockedStatus } from './types';

interface DataUploadDashboardProps {
  client: { id: string; name?: string; token: string; permissions: string[] };
  adminRoles: string[];
  adminToken: string;
}

// TODO: needs fixing when to display, especially after table selection
const SHOW_RECALCULATION_COMPONENT_DISABLED = true;

const SKIP_STRUCTURE_VALIDATION_AND_MAPPING = true;
const SKIP_DATA_MERGE = true;

const DataUploadDashboard: React.FC<DataUploadDashboardProps> = ({
  client,
  adminRoles,
  adminToken,
}) => {
  const [expertModeActive, setExpertModeActive] = useState<boolean>(false);
  const [dataCollName, setDataCollName] = useState<string>();
  const [hasBasisUploadTable, setHasBasisUploadTable] = useState<boolean>(false);
  const [hasBasisTable, setHasBasisTable] = useState<boolean>(false);
  const [hasTableDiffs, setHasTableDiffs] = useState<boolean>(false);
  const [allStructuralMappingsApplied, setAllStructuralMappingsApplied] = useState<boolean>(false);
  const [dataCollBlockedStatus, setDataCollBlockedStatus] = useState<DataCollBlockedStatus>();
  const [hasBackupTable, setHasBackupTable] = useState<boolean>(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [allDiffsResolved, setAllDiffsResolved] = useState<boolean>(false);
  const [allDiffsApplied, setAllDiffsApplied] = useState<boolean>(false);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [validationLoading, setValidationLoading] = useState(false);
  const validationResolverRef = useRef<null | ((result: any) => void)>(null);
  const [showRecalc, setShowRecalc] = useState(false);

  // Ref to store child's clearTransformationStatus function
  const childClearTransformationStatusRef = useRef<(() => void) | null>(null);

  // Memoize the status change callback to prevent unnecessary re-renders
  const handleStatusChange = useCallback(
    (status: any) => {
      // Show recalc card when transformation completed
      if (
        !SHOW_RECALCULATION_COMPONENT_DISABLED &&
        status?.status?.toLowerCase() === 'completed' &&
        (client?.permissions.includes('recalculate') ||
          client?.permissions.includes('recalculate_objects'))
      ) {
        setShowRecalc(true);
      } else {
        setShowRecalc(false);
      }
    },
    [client?.permissions]
  );

  // Use transformation hook for client mode
  const {
    transformationTriggered,
    transformationTriggeredAt,
    handleGenerate,
    clearTransformationStatus,
  } = useTransformation({
    dataCollName,
    client,
    onStatusChange: (status) => {
      // Auto-unblock when transformation completes successfully
      if (status?.status === 'completed') {
        updateBlockedStatus(false).catch((error) => {
          // Silent failure - blocking will eventually expire or admin can clear it
          console.error('Failed to auto-unblock after transformation:', error);
        });
      }
    },
  });

  // Combined clear function that clears both parent and child transformation states
  const combinedClearTransformationStatus = useCallback(() => {
    clearTransformationStatus(); // Clear parent's transformation status
    if (childClearTransformationStatusRef.current) {
      childClearTransformationStatusRef.current(); // Clear child's transformation status
    }
  }, [clearTransformationStatus]);
  const { updateBlockedStatus } = useDataCollBlockedStatus({
    dataCollName,
    client,
    setDataCollBlockedStatus,
    adminRoles,
    adminToken,
    currentBlockedStatus: dataCollBlockedStatus,
  });

  // Function to trigger a fresh validation and return a promise that resolves with the result
  const triggerValidationRefresh = () => {
    setPreviewRefreshKey((prev) => prev + 1);
    return new Promise((resolve) => {
      validationResolverRef.current = resolve;
    });
  };

  const handleStartEditing = () => {
    Modal.confirm({
      title: `Bearbeitung fortsetzen?`,
      content: `Möchten Sie die Bearbeitung der Tabelle ${dataCollName} fortsetzen? Dies wird andere Benutzer daran hindern, die Tabelle zu bearbeiten.`,
      onOk: () => {
        updateBlockedStatus(true)
          .then(() => {
            message.success('Die Tabelle wurde erfolgreich zur Bearbeitung blockiert.');
          })
          .catch((error) => {
            message.error('Fehler beim Blockieren der Tabelle: ' + error.message, 3);
            Sentry.captureException(error);
          });
      },
      okText: 'Ja, Bearbeitung fortsetzen',
      cancelText: 'Abbrechen',
    });
  };

  return (
    <>
      <Typography.Title level={1}>Stammdaten Upload für {client.name}</Typography.Title>
      {adminRoles.includes('data_admin') && (
        <Button
          type="primary"
          onClick={() => setExpertModeActive(!expertModeActive)}
          style={{ marginBottom: 12 }}
        >
          {expertModeActive ? 'Kundenmodus aktivieren' : 'Expertenmodus aktivieren'}
        </Button>
      )}
      <DataCollSelection
        client={client}
        dataCollName={dataCollName}
        setHasBasisUploadTable={setHasBasisUploadTable}
        setHasBasisTable={setHasBasisTable}
        setDataCollName={setDataCollName}
        setHasBackupTable={setHasBackupTable}
        setAllDiffsApplied={setAllDiffsApplied}
        setAllStructuralMappingsApplied={setAllStructuralMappingsApplied}
      />

      <DataCollBlockedStatusDisplay
        dataCollName={dataCollName}
        dataCollBlockedStatus={dataCollBlockedStatus}
        setDataCollBlockedStatus={setDataCollBlockedStatus}
        client={client}
        adminRoles={adminRoles}
        adminToken={adminToken}
        styles={{ marginBottom: 12 }}
      />
      {/* TODO: add further actions, when editing is begins. */}
      {/* Currently a user can unblock, then multiple users can edit. */}
      {expertModeActive && hasBasisUploadTable && !dataCollBlockedStatus?.isBlocked && (
        <Tooltip title="Strukturelle Bearbeitung der Tabelle für andere Benutzer sperren.">
          <Button type="primary" onClick={handleStartEditing} style={{ marginBottom: 12 }}>
            Bearbeitung fortsetzen
          </Button>
        </Tooltip>
      )}
      {dataCollName && (
        <div style={{ marginBottom: 12 }}>
          <DataCollBasisDownload
            dataCollName={dataCollName}
            client={client}
            hasBasisTable={hasBasisTable}
          />
        </div>
      )}
      {dataCollName && isEditable(dataCollBlockedStatus) && (
        <DataCollBasisUpload
          dataCollName={dataCollName}
          client={client}
          setDataCollBlockedStatus={setDataCollBlockedStatus}
          setHasBasisUploadTable={setHasBasisUploadTable}
          hasBasisUploadTable={hasBasisUploadTable}
          dataCollBlockedStatus={dataCollBlockedStatus}
          setHasTableDiffs={setHasTableDiffs}
          hasTableDiffs={hasTableDiffs}
          setAllStructuralMappingsApplied={setAllStructuralMappingsApplied}
          onUploadSuccess={triggerValidationRefresh}
          expertModeActive={expertModeActive}
          handleGenerate={handleGenerate}
          clearTransformationStatus={combinedClearTransformationStatus}
        />
      )}
      {/* Validation status display and API call */}
      <div id="basis-validation-section">
        <DataCollBasisValidation
          dataCollName={dataCollName || ''}
          client={client}
          expertModeActive={expertModeActive}
          hasBasisUploadTable={hasBasisUploadTable}
          refreshKey={previewRefreshKey}
          onValidationResult={(result, loading) => {
            setValidationResult(result);
            setValidationLoading(loading);
            if (!loading && validationResolverRef.current) {
              validationResolverRef.current(result);
              validationResolverRef.current = null;
            }
          }}
          onRunValidation={triggerValidationRefresh}
          style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }}
        />
      </div>

      {!SKIP_STRUCTURE_VALIDATION_AND_MAPPING && (
        <>
          {hasBasisTable && hasBasisUploadTable && isEditable(dataCollBlockedStatus) && (
            <div id="structural-mappings-section">
              {expertModeActive ? (
                <TableMergeStructureSection
                  dataCollName={dataCollName}
                  hasBasisUploadTable={hasBasisUploadTable}
                  client={client}
                  allStructuralMappingsApplied={allStructuralMappingsApplied}
                  onAllStructuralMappingsApplied={setAllStructuralMappingsApplied}
                  dataCollBlockedStatus={dataCollBlockedStatus}
                  setDataCollBlockedStatus={setDataCollBlockedStatus}
                  hasTableDiffs={hasTableDiffs}
                  setHasTableDiffs={setHasTableDiffs}
                  style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }}
                  onMappingsApplied={() => setPreviewRefreshKey((prev) => prev + 1)}
                />
              ) : (
                <TableMergeStructureStatusAlert
                  dataCollName={dataCollName}
                  client={client}
                  hasBasisUploadTable={hasBasisUploadTable}
                  onAllStructuralMappingsApplied={setAllStructuralMappingsApplied}
                />
              )}
            </div>
          )}
        </>
      )}
      {!SKIP_DATA_MERGE && (
        <>
          {hasBasisTable &&
            hasBasisUploadTable &&
            isEditable(dataCollBlockedStatus) &&
            (allStructuralMappingsApplied || expertModeActive) && (
              <div id="data-merge-section">
                <TableMergeData
                  dataCollName={dataCollName}
                  client={client}
                  dataCollBlockedStatus={dataCollBlockedStatus}
                  setDataCollBlockedStatus={setDataCollBlockedStatus}
                  hasTableDiffs={hasTableDiffs}
                  setHasTableDiffs={setHasTableDiffs}
                  style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }}
                  onDiffsApplied={() => setPreviewRefreshKey((prev) => prev + 1)}
                  setAllDiffsApplied={setAllDiffsApplied}
                  onAllDiffsResolved={setAllDiffsResolved}
                  allDiffsApplied={allDiffsApplied}
                  allStructuralMappingsApplied={allStructuralMappingsApplied}
                  expertModeActive={expertModeActive}
                  hasBasisTable={hasBasisTable}
                />
              </div>
            )}
        </>
      )}
      {/* {isEditable(dataCollBlockedStatus) && allStructuralMappingsApplied && hasTableDiffs && ( */}
      {/* TODO: revalidate when to display this card. more complex then current implementation, because might want to have editable actiev base or editable upload table... */}
      {(hasBasisUploadTable || hasBasisTable) && isDataEditable(dataCollBlockedStatus) && (
        <div id="upload-table-editing-card">
          <UploadTableEditingCard
            dataCollName={dataCollName}
            client={client}
            style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }}
            refreshKey={previewRefreshKey}
            hasBasisTable={hasBasisTable}
            hasBasisUploadTable={hasBasisUploadTable}
            isEditable={isDataEditable(dataCollBlockedStatus) || false}
            expertModeActive={expertModeActive}
          />
        </div>
      )}
      {/* TODO: adapt or remove when backupn and restore backend are ready */}
      {hasBasisUploadTable &&
        isEditable(dataCollBlockedStatus) &&
        allStructuralMappingsApplied &&
        hasBackupTable && (
          <RestorePreviousOriginalTableCard
            dataCollName={dataCollName}
            client={client}
            style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }}
          />
        )}
      {dataCollName &&
        isEditable(dataCollBlockedStatus) &&
        (hasBasisTable || hasBasisUploadTable) && (
          <>
            {expertModeActive ? (
              <DataCollTransformationPanel
                dataCollName={dataCollName}
                client={client}
                expertModeActive={expertModeActive}
                validationResult={validationResult}
                validationLoading={validationLoading}
                triggerValidationRefresh={triggerValidationRefresh}
                setShowRecalc={setShowRecalc}
                allDiffsResolved={allDiffsResolved}
                hasTableDiffs={hasTableDiffs}
                allDiffsApplied={allDiffsApplied}
                hasBasisUploadTable={hasBasisUploadTable}
                allStructuralMappingsApplied={allStructuralMappingsApplied}
                hasBasisTable={hasBasisTable}
                style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' }}
                hideStructureValidationAlerts={SKIP_STRUCTURE_VALIDATION_AND_MAPPING}
                clearTransformationStatus={clearTransformationStatus}
                childClearTransformationStatusRef={childClearTransformationStatusRef}
              />
            ) : (
              <TransformationStatus
                dataCollName={dataCollName}
                client={client}
                transformationTriggered={transformationTriggered}
                transformationTriggeredAt={transformationTriggeredAt}
                hasBasisUploadTable={hasBasisUploadTable}
                onStatusChange={handleStatusChange}
                style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)', margin: '16px 0' }}
              />
            )}
          </>
        )}
      {showRecalc &&
        (client?.permissions.includes('recalculate') ||
          client?.permissions.includes('recalculate_objects')) && (
          <RecalculationCard client={client} year={extractYear(dataCollName)} />
        )}
    </>
  );
};

export default DataUploadDashboard;
